import * as React from "react";
import {
  Title2,
  Body1,
  Input,
  Button,
  Dropdown,
  Option,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Recipient } from "../types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  recipientRow: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
  },
});

const ROLES = [
  { value: "signer", label: "Signer" },
  { value: "approver", label: "Approver" },
  { value: "cc", label: "CC" },
] as const;

interface Props {
  recipients: Recipient[];
  signingOrder: "sequential" | "parallel";
  onNext: (recipients: Recipient[], signingOrder: "sequential" | "parallel") => void;
  onBack: () => void;
}

const RecipientEntry: React.FC<Props> = ({ recipients: initialRecipients, signingOrder: initialOrder, onNext, onBack }) => {
  const styles = useStyles();
  const [recipients, setRecipients] = React.useState<Recipient[]>(initialRecipients);
  const [signingOrder, setSigningOrder] = React.useState(initialOrder);
  const [errors, setErrors] = React.useState<string[]>([]);

  const updateRecipient = (index: number, field: keyof Recipient, value: string) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
  };

  const addRecipient = () => {
    setRecipients([...recipients, { name: "", email: "", role: "signer" }]);
  };

  const removeRecipient = (index: number) => {
    if (recipients.length <= 1) return;
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];
    recipients.forEach((r, i) => {
      if (!r.name.trim()) newErrors.push(`Recipient ${i + 1}: Name is required`);
      if (!r.email.trim() || !r.email.includes("@")) newErrors.push(`Recipient ${i + 1}: Valid email is required`);
    });
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext(recipients, signingOrder);
    }
  };

  return (
    <div className={styles.container}>
      <Title2>Add Recipients</Title2>
      <Body1>Who needs to sign this document?</Body1>

      {errors.length > 0 && (
        <div style={{ color: tokens.colorPaletteRedForeground1 }}>
          {errors.map((e, i) => (
            <Body1 key={i} block>{e}</Body1>
          ))}
        </div>
      )}

      {recipients.map((recipient, index) => (
        <div key={index} className={styles.recipientRow}>
          <div className={styles.rowHeader}>
            <Body1 weight="semibold">Recipient {index + 1}</Body1>
            {recipients.length > 1 && (
              <Button size="small" appearance="subtle" onClick={() => removeRecipient(index)}>
                Remove
              </Button>
            )}
          </div>
          <Input
            placeholder="Name"
            value={recipient.name}
            onChange={(_, data) => updateRecipient(index, "name", data.value)}
          />
          <Input
            placeholder="Email"
            type="email"
            value={recipient.email}
            onChange={(_, data) => updateRecipient(index, "email", data.value)}
          />
          <Dropdown
            placeholder="Role"
            value={ROLES.find((r) => r.value === recipient.role)?.label || ""}
            onOptionSelect={(_, data) => updateRecipient(index, "role", data.optionValue as string)}
          >
            {ROLES.map((role) => (
              <Option key={role.value} value={role.value}>
                {role.label}
              </Option>
            ))}
          </Dropdown>
        </div>
      ))}

      <Button appearance="outline" onClick={addRecipient}>
        + Add Recipient
      </Button>

      <Switch
        label="Parallel signing (all at once)"
        checked={signingOrder === "parallel"}
        onChange={(_, data) => setSigningOrder(data.checked ? "parallel" : "sequential")}
      />

      <div className={styles.actions}>
        <Button appearance="secondary" onClick={onBack}>
          Back
        </Button>
        <Button appearance="primary" onClick={handleNext}>
          Review
        </Button>
      </div>
    </div>
  );
};

export default RecipientEntry;
